import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { ShieldCheck, Lock, Database, UserCheck } from "lucide-react";

const highlights = [
  {
    icon: ShieldCheck,
    color: "text-indigo-600",
    title: "Data Protection",
    text:
      "We use administrative, technical, and physical safeguards, including restricted access controls, encryption standards, secure hosting practices, and monitoring procedures, to protect personal information against unauthorized access, misuse, alteration, disclosure, or destruction.",
  },
  {
    icon: Database,
    color: "text-purple-600",
    title: "Information We Collect",
    text:
      "We may collect personal and technical information including your name, email address, phone number, company details, billing details, correspondence records, IP address, browser information, device identifiers, and information you voluntarily submit through contact forms, service requests, or transactions.",
  },
  {
    icon: UserCheck,
    color: "text-green-600",
    title: "How We Use Data",
    text:
      "Your information is used to respond to inquiries, provide and improve services, manage customer relationships, process legitimate transactions, maintain security, meet legal obligations, and enhance website performance and user experience.",
  },
  {
    icon: Lock,
    color: "text-red-500",
    title: "Third-Party Sharing",
    text:
      "We do not sell personal information. We may share limited data with trusted service providers such as payment gateways, hosting providers, email platforms, analytics tools, or legal authorities where disclosure is required for service delivery, fraud prevention, or compliance with applicable law.",
  },
];

const sections = [
  {
    title: "Introduction",
    paragraphs: [
      "This Privacy Policy explains how Nexora Ventures collects, uses, processes, stores, and protects information obtained through this website and through related communications, service inquiries, and transactions. We respect your privacy and are committed to handling personal data responsibly, transparently, and in accordance with applicable laws and regulatory expectations in the United Arab Emirates.",
      "By using this website, submitting information to us, or engaging with our services, you acknowledge that you have read and understood this Privacy Policy. This policy should be read together with our Terms & Conditions and any other legal notices published on this website.",
    ],
  },
  {
    title: "Information We Collect",
    paragraphs: [
      "We may collect personal information that you voluntarily provide to us, including your full name, email address, telephone number, company name, business requirements, project details, billing details, and any other information you submit when contacting us, requesting a quotation, subscribing to communications, or using our services.",
      "We may also collect technical and usage information automatically when you visit the website, including your IP address, browser type, operating system, language settings, pages visited, referral source, date and time of access, device identifiers, and other diagnostic data used for analytics, fraud prevention, and system administration.",
      "Where payments are involved, transaction-related data may be processed through secure third-party payment infrastructure for authorization, verification, anti-fraud review, and record keeping.",
      "All credit/debit cards details and personally identifiable information will NOT be stored, sold, shared, rented or leased to any third parties.",
    ],
  },
  {
    title: "How We Use Your Information",
    paragraphs: [
      "We use collected information for legitimate business purposes including responding to your messages, preparing proposals, delivering requested services, administering contracts, sending service-related communications, providing customer support, and maintaining an accurate record of our dealings with you.",
      "Information may also be used to improve website functionality, evaluate service performance, personalize communications, troubleshoot technical issues, detect and prevent fraud or abuse, enforce our legal rights, and comply with statutory, regulatory, accounting, or reporting obligations applicable in the UAE or other lawful jurisdictions.",
      "If you contact us through forms or email, we may use your details to follow up on your inquiry, notify you about project progress, clarify requirements, or provide updates relevant to your requested services. We do not use your information for unlawful profiling or for unrelated commercial exploitation.",
    ],
  },
  {
    title: "Cookies and Tracking Technologies",
    paragraphs: [
      "This website may use cookies, pixels, local storage, analytics tags, and similar tracking technologies to recognize repeat visitors, understand browsing behavior, measure website performance, improve usability, maintain security, and remember certain preferences where technically necessary.",
      "Cookies may be session-based or persistent. Some cookies are essential for core website functionality, while others help us understand aggregate usage patterns and optimize our content and service presentation. Third-party analytics providers may set limited cookies or tracking tools in accordance with their own privacy standards.",
      "Most web browsers allow you to manage or disable cookies through browser settings. Please note that restricting certain cookies may affect website functionality, page performance, or the availability of some features.",
    ],
  },
  {
    title: "Payment Processing and Third-Party Services",
    paragraphs: [
      "We may engage trusted third-party service providers to support our business operations, including payment gateway providers, banks, hosting partners, customer communication tools, analytics vendors, cybersecurity platforms, and professional advisers. These providers are permitted to process information only to the extent reasonably necessary to perform their services or to comply with legal obligations.",
      "Payment transactions are processed through secure payment gateway partners and acquiring-bank infrastructure. We may receive limited transaction status information such as payment confirmation, authorization status, masked card details, or fraud-screening outcomes, but we do not retain full card credentials on our own systems.",
      "Third-party websites, plug-ins, or services linked from this website may operate under independent privacy practices. Users should review the privacy notices of those third parties before sharing information directly with them.",
    ],
  },
  {
    title: "Data Protection and Security Measures",
    paragraphs: [
      "We maintain reasonable technical and organizational security measures designed to protect personal information from unauthorized access, accidental loss, misuse, alteration, or unlawful disclosure. These measures may include transport encryption, secure access management, role-based permissions, password protections, software maintenance, firewall controls, logging, and internal review procedures.",
      "Although we take commercially reasonable steps to secure information, no method of internet transmission or electronic storage can be guaranteed to be completely secure. Users are therefore encouraged to transmit information responsibly and to notify us promptly if they suspect any unauthorized activity involving their communications with us.",
      "We also limit access to personal information to personnel, contractors, and service providers who have a legitimate need to know such information for business, operational, compliance, or support purposes and who are subject to confidentiality obligations where appropriate.",
    ],
  },
  {
    title: "Data Retention",
    paragraphs: [
      "We retain personal information only for as long as reasonably necessary to fulfill the purposes for which it was collected, including service delivery, customer relationship management, fraud prevention, legal compliance, accounting, dispute resolution, and enforcement of contractual rights.",
      "Retention periods may vary depending on the nature of the data, the sensitivity of the information, transaction history, and any applicable regulatory or legal record-keeping requirements. When data is no longer required, we will take reasonable steps to delete, anonymize, or securely dispose of it.",
    ],
  },
  {
    title: "User Rights",
    paragraphs: [
      "Subject to applicable law and reasonable identity verification, you may request access to the personal information we hold about you, request correction of inaccurate or incomplete information, request deletion of data where retention is no longer necessary, or object to certain processing activities where such rights are available.",
      "You may also request clarification regarding how your information is processed or shared. We may need to retain certain records where required for contractual, legal, security, audit, or dispute-resolution purposes. Requests may be declined or limited where permitted by applicable law or where they would adversely affect the rights of others.",
    ],
  },
  {
    title: "Children's Privacy",
    paragraphs: [
      "This website is intended for business users and general audiences and is not knowingly directed to children. We do not knowingly collect personal information from children without appropriate legal basis or parental authorization where required. If we become aware that information has been provided by a child inappropriately, we will take reasonable steps to delete it.",
    ],
  },
  {
    title: "Cross-Border Processing",
    paragraphs: [
      "Depending on the nature of our service providers or infrastructure, some information may be processed or accessed outside the UAE under appropriate contractual, security, and operational safeguards. Where cross-border processing occurs, we take reasonable steps to ensure that the information remains protected in a manner consistent with this Privacy Policy and applicable legal requirements.",
    ],
  },
  {
    title: "Policy Updates",
    paragraphs: [
      "We may revise this Privacy Policy from time to time to reflect business changes, service enhancements, legal developments, or regulatory requirements. Any updated version will be published on this website and will become effective from the date of posting unless otherwise stated.",
      "Users are encouraged to review this page periodically to remain informed about how their information is protected and handled. Continued use of the website after any update constitutes acknowledgment of the revised policy.",
    ],
  },
  {
    title: "Contact for Privacy Matters",
    paragraphs: [
      "If you have any questions, requests, or concerns regarding this Privacy Policy or the processing of your personal information, you may contact us through the communication details published on this website. We will make reasonable efforts to review and address legitimate privacy-related requests within a commercially reasonable timeframe.",
    ],
  },
];

function PrivacyPolicy() {
  return (
    <div className="font-sans bg-gradient-to-b from-gray-50 to-white text-gray-900">
      <Navbar />

      <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white py-20">
        <div className="max-w-5xl mx-auto text-center px-6">
          <h1 className="text-5xl font-extrabold mb-6 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            We respect your privacy and are committed to protecting your personal
            information with transparency, accountability, and strong security
            controls.
          </p>
        </div>

        <div className="absolute -top-10 -left-10 w-72 h-72 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-72 h-72 bg-pink-400 opacity-10 rounded-full blur-3xl"></div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-20 space-y-14">
        <div className="grid md:grid-cols-2 gap-8">
          {highlights.map(({ icon: Icon, color, title, text }) => (
            <div
              key={title}
              className="group bg-white/70 backdrop-blur-lg p-8 rounded-3xl shadow-lg hover:shadow-2xl transition duration-300 border border-gray-100"
            >
              <Icon className={`${color} w-10 h-10 mb-4 group-hover:scale-110 transition`} />
              <h2 className="text-2xl font-semibold mb-3">{title}</h2>
              <p className="text-gray-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-200"></div>

        <div className="space-y-8">
          {sections.map((section) => (
            <article
              key={section.title}
              className="bg-white rounded-3xl border border-gray-100 shadow-sm p-8 md:p-10"
            >
              <h2 className="text-3xl font-bold mb-5 text-slate-900">
                {section.title}
              </h2>
              <div className="space-y-4 text-gray-700 leading-8">
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-10 rounded-3xl shadow-inner">
          <h2 className="text-3xl font-bold mb-4 text-indigo-700">
            Your Rights
          </h2>
          <p className="text-gray-700 leading-relaxed max-w-4xl">
            You have full control over your data and may request access,
            correction, or deletion of your personal information, subject to
            applicable legal and operational requirements. Our team remains
            available to assist with privacy-related concerns and to review
            legitimate requests in good faith.
          </p>
        </div>

        <div className="text-center">
          <h3 className="text-xl font-semibold mb-3">
            Have questions about your privacy?
          </h3>
          <p className="text-gray-600 mb-6">
            Contact us anytime and we will respond as promptly as reasonably
            possible.
          </p>
          <button className="px-6 py-3 bg-indigo-600 text-white rounded-xl shadow-md hover:bg-indigo-700 transition">
            Contact Support
          </button>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
